import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  isPushSupported,
  getNotificationPermission,
  enablePushNotifications,
  hasActivePushSubscription
} from '../services/pushNotificationService';
import toast from 'react-hot-toast';

const PROMPT_STORAGE_KEY = 'vybe_push_prompt_dismissed';
const PROMPT_COUNT_KEY = 'vybe_push_prompt_count';
const MAX_PROMPTS = 3;

interface PushNotificationPromptProps {
  trigger: 'hosted' | 'joined' | 'message' | 'application';
}

const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ trigger }) => {
  const { currentUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    checkAndShowPrompt();
  }, [currentUser, trigger]);

  const checkAndShowPrompt = async () => {
    if (!currentUser || !isPushSupported()) {
      return;
    }

    // Check if permission is already granted or denied
    const permission = getNotificationPermission();
    if (permission !== 'default') {
      return;
    }

    // Check if user already has subscription
    const hasSub = await hasActivePushSubscription(currentUser.id);
    if (hasSub) {
      return;
    }

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem(PROMPT_STORAGE_KEY);
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show again for 7 days
      }
    }

    // Check prompt count
    const promptCount = parseInt(localStorage.getItem(PROMPT_COUNT_KEY) || '0');
    if (promptCount >= MAX_PROMPTS) {
      return; // Max prompts reached
    }

    // Show the prompt
    setVisible(true);
    localStorage.setItem(PROMPT_COUNT_KEY, (promptCount + 1).toString());
  };

  const handleEnable = async () => {
    if (!currentUser) return;

    setEnabling(true);
    try {
      await enablePushNotifications(currentUser.id);
      toast.success('Push notifications enabled!');
      setVisible(false);
      localStorage.removeItem(PROMPT_STORAGE_KEY);
      localStorage.removeItem(PROMPT_COUNT_KEY);
    } catch (error: any) {
      console.error('Failed to enable push:', error);
      if (error.message?.includes('permission denied')) {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to enable notifications');
      }
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(PROMPT_STORAGE_KEY, Date.now().toString());
  };

  if (!visible) {
    return null;
  }

  // Simple, consistent copy for all triggers
  const promptTitle = 'Get notified about messages and Vybe updates';
  const promptBody = "Stay connected even when the app is closed";

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
      <div className="bg-gradient-to-br from-pink-500/20 to-cyan-500/20 backdrop-blur-lg border border-pink-500/30 rounded-2xl p-5 shadow-2xl">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 flex items-center justify-center">
            <Bell size={24} className="text-white" />
          </div>

          <div className="flex-1 pr-6">
            <h3 className="text-white font-bold text-lg mb-1">
              {promptTitle}
            </h3>
            <p className="text-white/80 text-sm mb-4">
              {promptBody}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleEnable}
                disabled={enabling}
                className="flex-1 bg-gradient-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enabling ? 'Enabling...' : 'Enable Notifications'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 text-white/80 hover:text-white text-sm font-medium transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
